import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/contexts/LanguageContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["pending", "reviewing", "resolved", "dismissed"];
const ACTIONS = ["no_action", "warning", "suspend", "ban", "remove_content"];


function UserCard({ label, user, testId }) {
  const { t } = useI18n();
  if (!user) return null;
  return (
    <div className="nvl-card p-4" data-testid={testId}>
      <div className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {label}
      </div>
      <div className="flex items-center gap-3">
        {user.profile?.main_photo_url ? (
          <img src={user.profile.main_photo_url} alt=""
               className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-full"
               style={{ backgroundColor: "hsl(var(--muted))" }} />
        )}
        <div>
          <div className="font-medium">
            {user.profile?.display_name || user.email || "—"}
          </div>
          <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {user.email}
          </div>
          <div className="text-xs mt-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {user.account_status}
            {user.moderation_status && user.moderation_status !== "clean"
              ? ` · ${user.moderation_status}` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}


export default function AdminReportDetail() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  const [action, setAction] = useState("");
  const [actionReason, setActionReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/admin/reports/${reportId}`);
      setReport(r.data);
      setNotes(r.data.admin_notes || "");
      setStatus(r.data.status || "pending");
      setAction(r.data.action || "");
      setActionReason(r.data.action_reason || "");
    } catch (e) {
      setError(extractError(e, t("app.admin.moderation.loadError")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reportId]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        status: status || undefined,
        admin_notes: notes,
      };
      if (action) {
        body.action = action;
        body.action_reason = actionReason || null;
      }
      await api.patch(`/admin/reports/${reportId}`, body);
      toast.success(t("app.admin.moderation.detail.saved"));
      await load();
    } catch (e) {
      toast.error(extractError(e, t("app.admin.moderation.detail.saveError")));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}
           data-testid="admin-report-detail-loading">
        {t("common.loading")}
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm" style={{ color: "hsl(var(--destructive))" }}
           data-testid="admin-report-detail-error">
        {error}
      </div>
    );
  }
  if (!report) return null;

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(lang === "it" ? "it-IT" : "en-GB"); }
    catch { return iso; }
  };

  return (
    <div data-testid="admin-report-detail-page">
      <button
        onClick={() => navigate("/admin/moderation")}
        className="flex items-center gap-2 text-sm mb-4"
        style={{ color: "hsl(var(--nvl-ink-soft))" }}
        data-testid="admin-report-detail-back"
      >
        <ArrowLeft size={14} /> {t("app.admin.nav.moderation")}
      </button>
      <p className="nvl-overline" style={{ color: "hsl(var(--nvl-accent))" }}>
        #{report.id}
      </p>
      <h1 className="mt-2 text-3xl md:text-4xl">
        {t("app.admin.moderation.detail.title")}
      </h1>
      <p className="mt-2 text-sm" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
        {fmt(report.created_at)}
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <UserCard label={t("app.admin.moderation.detail.reporter")}
                  user={report.reporter}
                  testId="admin-report-reporter" />
        <UserCard label={t("app.admin.moderation.detail.reported")}
                  user={report.reported}
                  testId="admin-report-reported" />
      </div>

      <div className="mt-6 nvl-card p-6 space-y-4">
        <div>
          <div className="nvl-overline mb-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.reason")}
          </div>
          <div className="font-medium">
            {t(`app.moderation.reasons.${report.reason}`, {}, report.reason)}
          </div>
        </div>
        {report.description ? (
          <div>
            <div className="nvl-overline mb-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.moderation.detail.description")}
            </div>
            <p className="whitespace-pre-wrap text-sm">{report.description}</p>
          </div>
        ) : null}
        {report.match_context ? (
          <div>
            <div className="nvl-overline mb-1" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.moderation.detail.matchContext")}
            </div>
            <p className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              #{report.match_context.match_id} · {report.match_context.status}
            </p>
          </div>
        ) : null}
        {report.message_context ? (
          <div className="nvl-card p-4" style={{ backgroundColor: "hsl(var(--muted))" }}>
            <div className="nvl-overline mb-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.moderation.detail.messageContext")}
            </div>
            {report.message_context.body ? (
              <p className="whitespace-pre-wrap text-sm">{report.message_context.body}</p>
            ) : null}
            {report.message_context.attachment_kind ? (
              <div className="text-xs mt-2" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
                {report.message_context.attachment_kind}
                {report.message_context.attachment_url ? (
                  <>{" "}
                    <a href={report.message_context.attachment_url}
                       target="_blank" rel="noreferrer" className="underline">
                      open
                    </a>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="text-xs" style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.noMessageContext")}
          </div>
        )}
      </div>

      <div className="mt-6 nvl-card p-6 space-y-4">
        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.statusLabel")}
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="nvl-input mt-2"
            data-testid="admin-report-status"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`app.admin.moderation.status.${s}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.actionLabel")}
          </label>
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="nvl-input mt-2"
            data-testid="admin-report-action"
          >
            <option value="">{t("app.admin.moderation.detail.noAction")}</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {t(`app.admin.moderation.action.${a}`)}
              </option>
            ))}
          </select>
        </div>

        {action ? (
          <div>
            <label className="text-xs uppercase tracking-widest"
                   style={{ color: "hsl(var(--nvl-ink-soft))" }}>
              {t("app.admin.moderation.detail.actionReasonLabel")}
            </label>
            <input
              value={actionReason}
              onChange={(e) => setActionReason(e.target.value)}
              className="nvl-input mt-2"
              data-testid="admin-report-action-reason"
              maxLength={500}
            />
          </div>
        ) : null}

        <div>
          <label className="text-xs uppercase tracking-widest"
                 style={{ color: "hsl(var(--nvl-ink-soft))" }}>
            {t("app.admin.moderation.detail.adminNotesLabel")}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="nvl-input mt-2 min-h-[120px]"
            placeholder={t("app.admin.moderation.detail.adminNotesPlaceholder")}
            data-testid="admin-report-notes"
            maxLength={4000}
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="nvl-btn nvl-btn-primary disabled:opacity-50"
            data-testid="admin-report-save"
          >
            {saving ? t("common.loading") : t("app.admin.moderation.detail.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
